import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { routes } from "../../utils/routes";
import { useAppDispatch } from "../../store/configureStore";
import {
  requestEmailOtp,
  requestSmsOtp,
  userCompletion,
} from "../../store/auth";
import {
  minAsyncDelay,
  MINIMUM_SPINNER_DELAY_MS,
} from "../../utils/minAsyncDelay";
import { css } from "@kivra/react-components";
import type { BECompletionV4 } from "@kivra/sdk/types/core/completion";
import { VerificationScreen } from "./VerificationScreen";
import { OtpScreen } from "./OtpScreen";
import { InputScreen } from "./InputScreen";
import { captureException } from "@kivra/sdk/log/sentry-react";

interface CompletionProps {
  completions: BECompletionV4[];
  onPartCompletionSuccessful?(type: BECompletionV4["type"]): void;
  onCompletionSuccessful(): void;
}

export function Completion(props: CompletionProps) {
  const [currentCompletionStep, setCurrentCompletionStep] = useState<number>(0);
  const history = useHistory();

  const hasNextScreen = currentCompletionStep < props.completions.length - 1;

  function onAbort(completion: BECompletionV4): void {
    // If the user tried to abort a mandatory completion we redirect them
    if (completion.level === "mandatory") {
      return history.push(routes.ROOT);
    }

    onSuccess(completion.type);
  }

  function onSuccess(type: BECompletionV4["type"]): void {
    // Notify that a completion step has been successful
    props.onPartCompletionSuccessful?.(type);

    if (hasNextScreen) {
      // Go to next completion step
      setCurrentCompletionStep((currentStep) => currentStep + 1);
    } else {
      // Notify that all completion steps have been successful
      props.onCompletionSuccessful();
    }
  }

  const completionData = props.completions[currentCompletionStep];
  if (!completionData) return null;
  return (
    <div
      className={css({
        // Too many wrappers, remove backgrounds
        display: "flex",
        flexDirection: "column",
        minWidth: "250px",
      })}
    >
      <CompletionStep
        // If the completion data changes (i.e. when you finished one
        // completion and you go on to the next) we don't want React to
        // keep any data from the previous completion. In order to
        // clean it in between completions we're setting a key to force
        // a full re-render.
        key={completionData.type}
        completion={completionData}
        onAbort={onAbort}
        onError={(err) => {
          captureException(err);
        }}
        onSuccess={() => onSuccess(completionData.type)}
      />
    </div>
  );
}

interface CompletionStepProps {
  onSuccess(): void;
  onError(error: Error): void;
  onAbort(completion: BECompletionV4): void;
  completion: BECompletionV4;
}

const SCREENS: { [key in Screen]: key } = {
  INPUT: "INPUT",
  OTP: "OTP",
  VERIFY: "VERIFY",
};

type Screen = "INPUT" | "OTP" | "VERIFY";

/**
 * Takes the different contexts from the BE and translates
 * them to specific completion screens.
 */
function deriveScreenFromCompletion(completion: BECompletionV4): Screen {
  switch (completion.context) {
    case "registration":
    case "takeover":
    case "never_verified":
    case "awakened":
      return SCREENS.INPUT;
    case "outdated":
      return completion.current ? SCREENS.VERIFY : SCREENS.INPUT;
    default:
      throw new Error(
        `No screen matches completion context ${completion.context}
                for type ${completion.type}`,
      );
  }
}

/**
 * This component renders a screen based of whatever completion
 * step the user needs to do.
 * What screen should be shown initially is derived from the completion context.
 */
function CompletionStep(props: CompletionStepProps) {
  const [screen, setScreen] = useState<Screen>(
    deriveScreenFromCompletion(props.completion),
  );
  const [inputValue, setInputValue] = useState<string | null>(null);
  const { postCompletionRequest, smsOtpRequest, emailOtpRequest } =
    userCompletionFormActions();

  function onAbort() {
    // If the completion is optional, postpone the completion on the BE
    if (props.completion.level === "optional") {
      // Use artificial minimum delay for improved UX
      // (no flickering button)
      return minAsyncDelay(
        postCompletionRequest({
          action: "postpone",
          context: props.completion.context,
          type: props.completion.type,
        }),
        MINIMUM_SPINNER_DELAY_MS,
      )
        .then(() => props.onAbort(props.completion))
        .catch(props.onError);
    } else {
      props.onAbort(props.completion);
    }
  }

  switch (screen) {
    case SCREENS.INPUT:
      return (
        <InputScreen
          completion={props.completion}
          onAbort={onAbort}
          onSuccess={(value: React.SetStateAction<string | null>) => {
            setInputValue(value);
            setScreen(SCREENS.OTP);
          }}
          onError={props.onError}
          requestEmailOtp={(email: string) => emailOtpRequest(email)}
          requestPhoneOtp={(phone: string) => smsOtpRequest(phone)}
        />
      );

    case SCREENS.OTP:
      return (
        <OtpScreen
          completion={props.completion}
          onAbort={onAbort}
          requestEmailOtp={(email: string) =>
            // Bubble the error to parent.
            emailOtpRequest(email).catch(props.onError)}
          requestPhoneOtp={(phone: string) =>
            // Bubble the error to parent.
            smsOtpRequest(phone).catch(props.onError)}
          onSuccess={props.onSuccess}
          value={inputValue!}
        />
      );

    case SCREENS.VERIFY:
      return (
        <VerificationScreen
          completion={props.completion}
          onAbort={() => {
            setScreen(SCREENS.INPUT);
          }}
          onError={props.onError}
          onSuccess={props.onSuccess}
        />
      );

    default:
      throw new Error(
        `Unspecified ${props.completion.type} screen state.`,
      );
  }
}

const userCompletionFormActions = () => {
  const dispatch = useAppDispatch();
  const postCompletionRequest = (
    completion:
      & Pick<BECompletionV4, "context" | "type">
      & ({ action: "postpone" } | { action: "confirm"; confirmed: string }),
  ) => dispatch(userCompletion(completion));
  const smsOtpRequest = (phoneNumber: string) =>
    dispatch(requestSmsOtp(phoneNumber));
  const emailOtpRequest = (email: string) => dispatch(requestEmailOtp(email));
  return { postCompletionRequest, smsOtpRequest, emailOtpRequest };
};
